import React, { useMemo } from 'react';
import { IonIcon, IonSkeletonText } from '@ionic/react';
import { calendarOutline, arrowUp, arrowDown, removeOutline } from 'ionicons/icons';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { useAttendanceSummary } from '@/hooks/useAttendance';
import type { AttendanceSummaryEntry } from '@/types/attendance';

interface GroupAttendancePulseProps {
  groupId: string | undefined;
  memberCount: number;
}

/**
 * Small pulse card for the group-detail sidebar.
 *
 * Shows a compact attendance signal:
 *  - rate of the last held meeting (big number)
 *  - sparkline of the last 8 meetings
 *  - average + trend arrow vs the prior half of the window
 *
 * Degrades gracefully when there are no meetings yet.
 */
/** Skip today's "phantom" meetings — held but nothing recorded (dust from
 *  resolve()). Past empty meetings are left alone: in headcount mode the
 *  counts stay 0 forever, so filtering them would erase real history. */
function isPhantomEntry(m: AttendanceSummaryEntry): boolean {
  if (m.status !== 'held') return false;
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  if (m.meeting_date !== today.toISOString().slice(0, 10)) return false;
  if ((m.present_count + m.absent_count + m.excused_count) > 0) return false;
  if (m.headcount && m.headcount > 0) return false;
  return true;
}

const GroupAttendancePulse: React.FC<GroupAttendancePulseProps> = ({ groupId, memberCount }) => {
  const { data: summary, isLoading } = useAttendanceSummary(groupId, 8);
  const meetings: AttendanceSummaryEntry[] = useMemo(
    () => (summary?.data ?? []).filter((m) => !isPhantomEntry(m)),
    [summary],
  );
  const mode = summary?.attendance_mode ?? 'roster';

  const stats = useMemo(() => {
    if (meetings.length === 0) return null;

    const heldMeetings = meetings.filter((m) => m.status === 'held');
    const lastHeld = [...heldMeetings].reverse()[0] ?? null;

    const rateOf = (m: AttendanceSummaryEntry): number => {
      if (mode === 'headcount') {
        if (!memberCount || !m.headcount) return 0;
        return Math.min(100, Math.round((m.headcount / memberCount) * 100));
      }
      const known = m.present_count + m.absent_count + m.excused_count;
      if (!known) return 0;
      return Math.round((m.present_count / known) * 100);
    };

    const heldRates = heldMeetings.map(rateOf);
    const avg = heldRates.length
      ? Math.round(heldRates.reduce((a, b) => a + b, 0) / heldRates.length)
      : 0;

    // Trend: compare the newer half to the older half of *held* meetings only.
    let trend: 'up' | 'down' | 'flat' = 'flat';
    if (heldRates.length >= 4) {
      const mid = Math.floor(heldRates.length / 2);
      const olderAvg = heldRates.slice(0, mid).reduce((a, b) => a + b, 0) / mid;
      const newerAvg = heldRates.slice(mid).reduce((a, b) => a + b, 0) / (heldRates.length - mid);
      const diff = newerAvg - olderAvg;
      if (diff > 3) trend = 'up';
      else if (diff < -3) trend = 'down';
    }

    return {
      lastHeld,
      lastRate: lastHeld ? rateOf(lastHeld) : null,
      avg,
      trend,
      rateOf,
    };
  }, [meetings, mode, memberCount]);

  if (isLoading) {
    return (
      <div className="pulse-card">
        <div className="pulse-card__header">
          <h3 className="crm-section-title">Prezență</h3>
          <p className="crm-section-subtitle">Ultimele întâlniri</p>
        </div>
        <IonSkeletonText animated style={{ width: '100%', height: 60, borderRadius: 10 }} />
      </div>
    );
  }

  if (!meetings.length) {
    return (
      <div className="pulse-card">
        <div className="pulse-card__header">
          <h3 className="crm-section-title">Prezență</h3>
          <p className="crm-section-subtitle">Ultimele întâlniri</p>
        </div>
        <div className="pulse-empty">
          <IonIcon icon={calendarOutline} />
          <div>
            <p className="pulse-empty__title">Încă fără întâlniri</p>
            <p className="pulse-empty__hint">Înregistrează prima prezență din fila alăturată.</p>
          </div>
        </div>
      </div>
    );
  }

  const maxRate = Math.max(...meetings.map((m) => stats?.rateOf(m) ?? 0), 1);

  return (
    <div className="pulse-card">
      <div className="pulse-card__header">
        <h3 className="crm-section-title">Prezență</h3>
        <p className="crm-section-subtitle">Ultimele {meetings.length} întâlniri</p>
      </div>

      {stats?.lastHeld ? (
        <div className="pulse-hero">
          <div
            className={`pulse-hero__rate pulse-hero__rate--${
              (stats.lastRate ?? 0) >= 80 ? 'good' :
              (stats.lastRate ?? 0) >= 50 ? 'mid' : 'low'
            }`}
          >
            {stats.lastRate}
            <span className="pulse-hero__pct">%</span>
          </div>
          <div className="pulse-hero__meta">
            <div className="pulse-hero__label">Ultima întâlnire</div>
            <div className="pulse-hero__date">
              {format(parseISO(stats.lastHeld.meeting_date), 'd MMM', { locale: ro })}
            </div>
            {mode === 'roster' && (
              <div className="pulse-hero__detail">
                {stats.lastHeld.present_count} din{' '}
                {stats.lastHeld.present_count + stats.lastHeld.absent_count + stats.lastHeld.excused_count}
              </div>
            )}
            {mode === 'headcount' && stats.lastHeld.headcount !== null && (
              <div className="pulse-hero__detail">
                {stats.lastHeld.headcount} prezenți
              </div>
            )}
          </div>
        </div>
      ) : null}

      <div className="pulse-spark" aria-label="Tendință prezență">
        {meetings.map((m) => {
          const rate = stats?.rateOf(m) ?? 0;
          const heightPct = Math.max(6, (rate / maxRate) * 100);
          const status = m.status;
          const dateLabel = format(parseISO(m.meeting_date), 'd MMM', { locale: ro });
          return (
            <div
              key={m.id}
              className={`pulse-spark__col pulse-spark__col--${status}`}
              title={`${dateLabel} — ${status === 'held' ? `${rate}%` : status}`}
            >
              <div className="pulse-spark__bar" style={{ height: `${heightPct}%` }} />
            </div>
          );
        })}
      </div>

      <div className="pulse-footer">
        <span className="pulse-footer__stat">
          Media <strong>{stats?.avg ?? 0}%</strong>
        </span>
        {stats?.trend && stats.trend !== 'flat' && (
          <span className={`pulse-footer__trend pulse-footer__trend--${stats.trend}`}>
            <IonIcon icon={stats.trend === 'up' ? arrowUp : stats.trend === 'down' ? arrowDown : removeOutline} />
            {stats.trend === 'up' ? 'în creștere' : 'în scădere'}
          </span>
        )}
      </div>
    </div>
  );
};

export default GroupAttendancePulse;
